import Roblox from 'Roblox';
import FriendsProvider from '../../providers/friends/friendsProvider';
import PrescenceProvider from '../../providers/presence/presenceProvider';
import { CacheCriteria } from '../../libs/constants';

const { EnvironmentUrls } = Roblox;

type UserStoreCallback = (data: object, prevCursor: string, nextCursor: string) => void;
type FriendObject = { id: number; presence: object };
type PresenceObject = { data: { userPresences: { userId: number } } };
type FriendsRes = {
  data: {
    data: Array<FriendObject>;
    previousPageCursor: string;
    nextPageCursor: string;
  };
};
type FriendsRequest = {
  callback: UserStoreCallback;
  userId: number;
  isGuest: boolean;
  cursor?: number;
  sortOrder?: string;
  limit?: number;
  cacheCriteria?: CacheCriteria;
};

type FriendsRefreshRequest = {
  userId: number;
  isGuest: boolean;
  cursor?: number;
  sortOrder?: string;
  limit?: number;
  cacheCriteria?: CacheCriteria;
};
type ApiUrls = Array<string>;
type FriendsData = { [key: string]: FriendObject };
type UserPresence = { userId: number };

const populateFriendsData = (friends: Array<FriendObject>) => {
  const friendsData: FriendsData = {};
  friends.forEach(friend => {
    friendsData[friend.id] = friend;
    friendsData[friend.id].presence = {};
  });
  return friendsData;
};

const populatePresenceData = (friendsData: FriendsData, presenceData: Array<PresenceObject>) => {
  if (!presenceData || presenceData.length === 0) return friendsData;
  const presences = presenceData.reduce((array, { data: { userPresences } }) => {
    return array.concat(userPresences);
  }, []);
  presences.forEach((userPresence: UserPresence) => {
    friendsData[userPresence.userId].presence = userPresence;
  });
};

const generateUserDataStoreMethod = (methodName: string) => {
  return ({
    callback,
    userId,
    cursor,
    sortOrder,
    limit,
    isGuest,
    cacheCriteria
  }: FriendsRequest) => {
    let unsubscribeToPresence: any;
    const unsubscribeToFriends: any = FriendsProvider[methodName](
      (friendsRes: FriendsRes) => {
        if (!friendsRes?.data) return callback([], null, null);
        const userData = friendsRes.data.data;
        const friendsData = populateFriendsData(userData);
        const prevCursor = friendsRes.data.previousPageCursor;
        const nextCursor = friendsRes.data.nextPageCursor;
        if (isGuest) {
          callback(userData, prevCursor, nextCursor);
        } else {
          unsubscribeToPresence = PrescenceProvider.getUserPresence(
            Object.keys(friendsData).map(id => parseInt(id)),
            (presencesData: Array<PresenceObject>) => {
              populatePresenceData(friendsData, presencesData);
              callback(userData, prevCursor, nextCursor);
            },
            cacheCriteria
          );
        }
      },
      userId,
      cursor,
      sortOrder,
      limit,
      cacheCriteria
    );
    return () => {
      unsubscribeToFriends();
      if (typeof unsubscribeToPresence === 'function') unsubscribeToPresence();
    };
  };
};

const generateUserDataStoreRefreshMethod = (methodName: string) => {
  return ({ userId, cursor, sortOrder, limit, isGuest, cacheCriteria }: FriendsRefreshRequest) => {
    return FriendsProvider[methodName](userId, cursor, sortOrder, limit, cacheCriteria).then(
      (friendsRes: FriendsRes) => {
        if (!friendsRes?.data) return {};
        const userData = friendsRes.data.data;
        const friendsData = populateFriendsData(userData);
        const prevCursor = friendsRes.data.previousPageCursor;
        const nextCursor = friendsRes.data.nextPageCursor;
        if (isGuest) {
          return { userData: userData, prev: prevCursor, next: nextCursor };
        }
        return PrescenceProvider.refreshUserPresence(
          Object.keys(friendsData).map(id => parseInt(id)),
          cacheCriteria
        ).then((presencesData: Array<PresenceObject>) => {
          populatePresenceData(friendsData, presencesData);
          return { userData: userData, prev: prevCursor, next: nextCursor };
        });
      }
    );
  };
};

const DATA_URL = {
  presence: EnvironmentUrls.presenceApi,
  friends: EnvironmentUrls.friendsApi
};

const clearUserDataStoreCache = (...apiUrls: ApiUrls) => {
  if (localStorage) {
    let userDataStoreUrls =
      apiUrls.length > 0 ? apiUrls : [EnvironmentUrls.friendsApi, EnvironmentUrls.presenceApi];
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const localStorageKey = localStorage.key(i);
      for (let j = 0; j < userDataStoreUrls.length; j++) {
        const userStoreUrl = userDataStoreUrls[j];
        if (localStorageKey.indexOf(userStoreUrl) > -1) {
          keysToRemove.push(localStorageKey);
        }
      }
    }
    for (let k = 0; k < keysToRemove.length; k++) {
      localStorage.removeItem(keysToRemove[k]);
    }
  }
};

const UserDataStore: { [key: string]: any } = {
  DATA_URL,
  subscribeToFriends: generateUserDataStoreMethod('getFriends'),
  subscribeToFollowers: generateUserDataStoreMethod('getFollowers'),
  subscribeToFollowings: generateUserDataStoreMethod('getFollowings'),
  subscribeToFriendsRequests: generateUserDataStoreMethod('getFriendsRequests'),
  refreshFriends: generateUserDataStoreRefreshMethod('refreshFriends'),
  refreshFollowers: generateUserDataStoreRefreshMethod('refreshFollowers'),
  refreshFollowings: generateUserDataStoreRefreshMethod('refreshFollowings'),
  refreshFriendsRequests: generateUserDataStoreRefreshMethod('refreshFriendsRequests'),
  clearUserDataStoreCache
};

export default UserDataStore;
